/**
 * @fileoverview Organism: the router's single errorElement.
 * @module components/organisms/RouteErrorElement
 *
 * @description
 * React Router hands loader/action failures to this component as `unknown`.
 * It normalises them, reports them through the central sink, then delegates
 * rendering to the shared ErrorTemplate so every error screen looks alike.
 */

import { isRouteErrorResponse, useRouteError } from "react-router-dom"

import { ErrorTemplate } from "@/components/templates"
import { AppError, codeFromStatus, logError } from "@/lib/errors"

/**
 * Route-level error boundary.
 *
 * @returns The shared error screen.
 */
export function RouteErrorElement() {
	const routeError = useRouteError()
	const isResponse = isRouteErrorResponse(routeError)

	// A router Response carries a status and a statusText worth keeping;
	// anything else is a plain bug and goes through the generic normaliser.
	const appError = logError(
		isResponse
			? new AppError(routeError.statusText || "Route error", {
					code: codeFromStatus(routeError.status),
					status: routeError.status,
					cause: routeError,
					context: { data: routeError.data as unknown },
				})
			: routeError,
		"router"
	)

	return (
		<ErrorTemplate
			error={appError}
			statusText={isResponse ? routeError.statusText : undefined}
		/>
	)
}
